import { IBrand, IMarket } from 'data/schema';
import {
  IFeatureFlags,
  ITenantConfig,
  tenantConfigSchema,
  flagsSchema,
  ISchedulerConfig,
  ISessionTimeoutConfig,
  IAccessControlConfig,
  IDccConfig,
  ISccConfig,
} from 'data/tenant-config';
import { sendApiRequest } from './network';
import { userSession } from 'state/session';
import { State } from './state';
import { ApiError } from './errors';
import { isDebugMode } from './ui/debug';

export interface ITenantIdentifier {
  brand?: IBrand;
  market?: IMarket;
  tenant?: string;
}

// Recursively freeze a value. Applied once to the shared default config so an accidental
// mutation of a returned nested member (e.g. getDcc()/getScc(), which hand back the shared
// reference) throws loudly instead of silently corrupting the default for every tenant.
function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value as Record<string, unknown>).forEach((child) => deepFreeze(child));
  }
  return value;
}

export class TenantConfig extends State implements ITenantIdentifier {
  // Memoized schema-default config, shared across instances. The default config is
  // immutable (read model), so a single parsed instance is safe to reuse.
  private static defaultConfig?: ITenantConfig;

  cache: Record<string, ITenantConfig>;
  brand!: IBrand;
  market!: IMarket;
  tenant!: string;
  isLoaded?: boolean = false;
  inactive = false;
  private isLoading = false;
  private flagOverrides: Partial<IFeatureFlags> = {};
  private schedulerOverrides: Partial<ISchedulerConfig> = {};
  private sessionTimeoutOverrides: Partial<ISessionTimeoutConfig> = {};
  private accessControlOverrides: Partial<IAccessControlConfig> = {};
  // The config that was active before the last re-key, with the brand and market it belongs to.
  // Bridges the loading window of a partner-only re-key (AVS-4461).
  private previousConfig?: { brand: IBrand; market: IMarket; config: ITenantConfig };

  // Promise that resolves when the config is loaded
  // This allows us to wait for the config to be loaded before rendering the app
  configLoaded: Promise<unknown>;

  // Function to resolve the promise when the config is loaded
  loadingSuccess: (value: unknown) => void = () => {};

  // Function to resolve the promise when the config loading fails
  loadingFailed: (value: unknown) => void = () => {};

  constructor(identifier?: ITenantIdentifier, initialData?: ITenantConfig) {
    super();
    this.cache = {};
    this.configLoaded = new Promise((resolve, reject) => {
      this.loadingSuccess = resolve;
      this.loadingFailed = reject;
    });
    // Awaiters still see a failed load; this only stops it reporting as unhandled where nothing awaits (DCC host).
    this.configLoaded.catch(() => undefined);

    this.setIdentifier(identifier, initialData);

    if (!identifier) {
      // identifier is not forced so use the session
      userSession().on('change', (keys) => {
        if (['brand', 'market', 'partnerToken'].some((key) => keys.includes(key))) {
          // added debugging logs for https://collaboration.msi.audi.com/jira/browse/AVS-3817. Logs will be removed after the issue is resolved
          this.setIdentifier();
        }
      });
    }
  }

  // This is cache identifier. Change of any of these values will invalidate the cache
  // We allow a custom identifier to be passed in so we can use it on the backend
  setIdentifier(identifier: ITenantIdentifier = {}, initialData?: ITenantConfig) {
    // Remember the loaded config served right now (never the defaults). Read from the cache, so a
    // cache-hit re-key that skipped setConfig() is tracked too.
    const activeConfig = this.cache[this.generateCacheKey()];
    if (activeConfig) {
      this.previousConfig = { brand: this.brand, market: this.market, config: activeConfig };
    }

    this.brand = identifier.brand ?? (userSession().get('brand') as IBrand);
    this.market = identifier.market ?? (userSession().get('market') as IMarket);
    this.tenant = identifier.tenant ?? (userSession().get('partnerToken') as string);

    if (initialData) {
      this.setConfig(initialData);
      return;
    }

    if (!this.brand || !this.market) {
      return;
    }

    this.loadConfig();
  }

  setConfig(config?: ITenantConfig) {
    if (config) {
      this.cache[this.generateCacheKey()] = config;
    }
    this.emit('change', ['config'], [config], [this.config]);
  }

  get config() {
    const cacheKey = this.generateCacheKey();
    const cached = this.cache[cacheKey];
    if (cached) {
      return cached;
    }

    // AVS-4461: on a partner-only re-key, keep serving the previously active config as a
    // best-effort bridge while the refetch is in flight — it beats the defaults, which read
    // `voip: false` and tore down a live dealer's VOIP. If the refetch fails, the bridge stays
    // in place. A brand or market change still reads the defaults.
    const previous = this.previousConfig;
    if (previous && previous.brand === this.brand && previous.market === this.market) {
      return previous.config;
    }

    // Return default config parsed from empty object to get schema defaults.
    // Parse once and reuse — before the tenant config loads, `config` (hence every
    // isAllowed() call) is read on every session change during startup; re-running the
    // Zod parse each time is pure waste.
    if (!TenantConfig.defaultConfig) {
      TenantConfig.defaultConfig = deepFreeze(tenantConfigSchema.parse({}));
    }
    return TenantConfig.defaultConfig;
  }

  // Load config from the server
  private async loadConfig() {
    const cacheKey = this.generateCacheKey();
    const cached = this.cache[cacheKey];
    if (cached) {
      this.isLoaded = true;
      return cached;
    }

    if (this.isLoading) {
      // The in-flight load reports its own failure through configLoaded; a waiter only reads the cache.
      await this.configLoaded.catch(() => undefined);
      return this.cache[cacheKey];
    }

    this.isLoading = true;

    try {
      const fetched = await this.fetchConfig();
      // A failed fetch is neither a loaded config (isLoaded) nor a disabled market (inactive).
      if (!fetched) {
        return undefined;
      }

      const data = tenantConfigSchema.parse(fetched);
      this.inactive = data.enabled === false;
      this.isLoaded = true;
      this.setConfig(data);
      this.loadingSuccess(data);

      if (!this.isAllowed('voip')) {
        userSession().setValues({ customerAllowed: true });
      }
      return data;
    } finally {
      this.isLoading = false;
    }
  }

  // Fetch feature flags from the server for a specific brand and market
  private async fetchConfig() {
    try {
      const data = await sendApiRequest<ITenantConfig>(`/config?brand=${this.brand}&market=${this.market}`, {
        method: 'GET',
      });

      return data;
    } catch (error) {
      // Handle abort errors gracefully - they're expected when requests are cancelled
      if (error instanceof ApiError && error.message.includes('aborted')) {
        console.error('Config request was cancelled:', error.message);
        return null;
      }
      console.error('Failed to fetch feature flags:', error);
      this.loadingFailed(error);
      return null;
    }
  }

  // Get a specific feature flag
  isAllowed(flag: keyof IFeatureFlags) {
    // Check overrides first
    if (flag in this.flagOverrides) {
      return this.flagOverrides[flag] ?? false;
    }

    const config = this.config;
    if (!config) {
      return false;
    }

    return config.flags[flag] ?? false;
  }

  // Set feature flag overrides that persist across API calls
  setFlagOverrides(overrides: Partial<IFeatureFlags>) {
    // Filter out undefined values and validate using Zod
    const filteredOverrides = Object.entries(overrides).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key as keyof IFeatureFlags] = value;
      }
      return acc;
    }, {} as Partial<IFeatureFlags>);

    const partialFlagsSchema = flagsSchema.partial().strict();
    const validationResult = partialFlagsSchema.safeParse(filteredOverrides);

    if (!validationResult.success) {
      const errors =
        validationResult.error?.issues?.map((err) => `${err.path.join('.')}: ${err.message}`).join(', ') ||
        'Unknown validation error';
      throw new Error(`Invalid feature flag overrides: ${errors}`);
    }

    const oldOverrides = { ...this.flagOverrides };
    this.flagOverrides = { ...this.flagOverrides, ...overrides };
    this.emit('change', ['flagOverrides'], [this.flagOverrides], [oldOverrides]);
  }

  // Clear all feature flag overrides
  clearFlagOverrides() {
    const oldOverrides = { ...this.flagOverrides };
    this.flagOverrides = {};
    this.emit('change', ['flagOverrides'], [this.flagOverrides], [oldOverrides]);
  }

  // Get current flag overrides
  getFlagOverrides() {
    return { ...this.flagOverrides };
  }

  /**
   * Get a top-level config value with type safety
   * @param key - The config key to retrieve
   * @returns The config value for the specified key
   *
   * @example
   * const scheduler = tenantConfig().getConfigValue('scheduler');
   * const flags = tenantConfig().getConfigValue('flags');
   */
  getConfigValue<K extends keyof ITenantConfig>(key: K): ITenantConfig[K] {
    return this.config[key];
  }

  /**
   * Get scheduler configuration with schema defaults
   * Supports overrides for testing
   *
   * @example
   * const { startWindowBeforeMinutes, minAdvanceMinutes } = tenantConfig().getScheduler();
   */
  getScheduler(): ISchedulerConfig {
    const baseConfig = this.config.scheduler;
    return { ...baseConfig, ...this.schedulerOverrides };
  }

  /**
   * Get session timeout configuration with schema defaults
   * Supports overrides for testing
   *
   * @example
   * const { timeoutMinutes, warningMinutes } = tenantConfig().getSessionTimeout();
   */
  getSessionTimeout(): ISessionTimeoutConfig {
    const baseConfig = this.config.sessionTimeout;
    return { ...baseConfig, ...this.sessionTimeoutOverrides };
  }

  /**
   * Get access control configuration with schema defaults
   * Supports overrides for testing
   *
   * @example
   * const { dealerWhitelistEnabled, dealerWhitelist } = tenantConfig().getAccessControl();
   */
  getAccessControl(): IAccessControlConfig {
    const baseConfig = this.config.accessControl;
    return { ...baseConfig, ...this.accessControlOverrides };
  }

  /**
   * Get DCC (Digital Car Configurator) configurations
   *
   * @example
   * const dccConfigs = tenantConfig().getDcc();
   */
  getDcc(): IDccConfig[] {
    return this.config.dcc;
  }

  /**
   * Get SCC (Sales Car Configurator) configurations
   *
   * @example
   * const sccConfigs = tenantConfig().getScc();
   */
  getScc(): ISccConfig[] {
    return this.config.scc;
  }

  /**
   * Set scheduler configuration overrides for testing
   * @param overrides - Partial scheduler config to override
   *
   * @example
   * tenantConfig().setSchedulerOverrides({ startWindowBeforeMinutes: 10 });
   */
  setSchedulerOverrides(overrides: Partial<ISchedulerConfig>) {
    const oldOverrides = { ...this.schedulerOverrides };
    this.schedulerOverrides = { ...this.schedulerOverrides, ...overrides };
    this.emit('change', ['schedulerOverrides'], [this.schedulerOverrides], [oldOverrides]);
  }

  /**
   * Set session timeout configuration overrides for testing
   * @param overrides - Partial session timeout config to override
   *
   * @example
   * tenantConfig().setSessionTimeoutOverrides({ timeoutMinutes: 10 });
   */
  setSessionTimeoutOverrides(overrides: Partial<ISessionTimeoutConfig>) {
    const oldOverrides = { ...this.sessionTimeoutOverrides };
    this.sessionTimeoutOverrides = { ...this.sessionTimeoutOverrides, ...overrides };
    this.emit('change', ['sessionTimeoutOverrides'], [this.sessionTimeoutOverrides], [oldOverrides]);
  }

  /**
   * Set access control configuration overrides for testing
   * @param overrides - Partial access control config to override
   *
   * @example
   * tenantConfig().setAccessControlOverrides({ dealerWhitelistEnabled: true });
   */
  setAccessControlOverrides(overrides: Partial<IAccessControlConfig>) {
    const oldOverrides = { ...this.accessControlOverrides };
    this.accessControlOverrides = { ...this.accessControlOverrides, ...overrides };
    this.emit('change', ['accessControlOverrides'], [this.accessControlOverrides], [oldOverrides]);
  }

  /**
   * Clear scheduler configuration overrides
   */
  clearSchedulerOverrides() {
    const oldOverrides = { ...this.schedulerOverrides };
    this.schedulerOverrides = {};
    this.emit('change', ['schedulerOverrides'], [this.schedulerOverrides], [oldOverrides]);
  }

  /**
   * Clear session timeout configuration overrides
   */
  clearSessionTimeoutOverrides() {
    const oldOverrides = { ...this.sessionTimeoutOverrides };
    this.sessionTimeoutOverrides = {};
    this.emit('change', ['sessionTimeoutOverrides'], [this.sessionTimeoutOverrides], [oldOverrides]);
  }

  /**
   * Clear access control configuration overrides
   */
  clearAccessControlOverrides() {
    const oldOverrides = { ...this.accessControlOverrides };
    this.accessControlOverrides = {};
    this.emit('change', ['accessControlOverrides'], [this.accessControlOverrides], [oldOverrides]);
  }

  /**
   * Clear all configuration overrides (flags, scheduler, sessionTimeout, accessControl)
   */
  clearAllOverrides() {
    this.clearFlagOverrides();
    this.clearSchedulerOverrides();
    this.clearSessionTimeoutOverrides();
    this.clearAccessControlOverrides();
  }

  // Private helper to generate cache keys
  private generateCacheKey() {
    return `${this.brand}_${this.market}_${this.tenant}`;
  }
}

let state: TenantConfig;

// Initialize the tenant config once, just when needed
export function tenantConfig() {
  if (!state) {
    state = new TenantConfig();
  }
  return state;
}

if (isDebugMode()) {
  // @ts-expect-error - expose config in global scope for debugging
  window.tenantConfig = tenantConfig;
}
